import {
  PIXEL_GENERATORS,
  QUALITY_LABEL,
  SPEED_LABEL,
  VIDEO_GENERATORS,
  allShots,
  lineTimings,
  videoPaths,
  type Act,
  type Character,
  type DialogueLine,
  type GeneratorInfo,
  type SfxCue,
  type Shot,
  type ShotStage,
  type StageState,
  type TimelineEntry,
  type VideoDoc,
  type VideoScene,
} from '@ai-studio/shared';

/**
 * Pure helpers of the Video Studio tab: storyboard edits (immutable), piece states as badges, the best clip of
 * a shot, the playhead on the timeline, audio lanes, and the sequence commands that preview engine shots in the
 * Scene view. No React, no network.
 */

export interface PieceInfo {
  readonly state: StageState;
  readonly request?: string;
  readonly file?: string;
}
export type Pieces = Readonly<Record<string, PieceInfo>>;

export const pieceState = (pieces: Pieces, key: string): StageState => pieces[key]?.state ?? 'none';

/* ------------------------------------------------------------------ */
/* Badges                                                              */

export interface Badge {
  readonly key: string;
  /** One or two letters on the card. */
  readonly short: string;
  readonly label: string;
  readonly state: StageState;
}

const SHOT_BADGES: readonly [ShotStage, string, string][] = [
  ['keyframe', 'K', 'Keyframe'],
  ['animatic', 'A', 'Animatic'],
  ['final', 'F', 'Final'],
  ['post', 'P', 'Post (interpolate / upscale)'],
];

/** The status badges of a shot card. */
export function shotBadges(shot: Shot, pieces: Pieces): Badge[] {
  if (shot.type === 'engine') return [{ key: `shot:${shot.id}:engine`, short: 'E', label: 'Engine recording', state: pieceState(pieces, `shot:${shot.id}:engine`) }];
  const out: Badge[] = SHOT_BADGES.map(([stage, short, label]) => ({ key: `shot:${shot.id}:${stage}`, short, label, state: pieceState(pieces, `shot:${shot.id}:${stage}`) }));
  if (shot.continuity === 'first-last') out.splice(1, 0, { key: `shot:${shot.id}:endKeyframe`, short: 'K2', label: 'End keyframe', state: pieceState(pieces, `shot:${shot.id}:endKeyframe`) });
  return out;
}

/** Voice / lip-sync / SFX badges of a shot. */
export function audioBadges(shot: Shot, pieces: Pieces): Badge[] {
  const out: Badge[] = [];
  for (const l of shot.dialogue ?? []) {
    out.push({ key: `line:${shot.id}:${l.id}:voice`, short: 'V', label: `Voice ${l.id}`, state: pieceState(pieces, `line:${shot.id}:${l.id}:voice`) });
    const lip = `line:${shot.id}:${l.id}:lipsync`;
    if (pieces[lip]) out.push({ key: lip, short: 'L', label: `Lip sync ${l.id}`, state: pieceState(pieces, lip) });
  }
  for (const c of shot.sfx ?? []) out.push({ key: `sfx:${shot.id}:${c.id}`, short: 'S', label: `SFX ${c.id}`, state: pieceState(pieces, `sfx:${shot.id}:${c.id}`) });
  return out;
}

const URGENCY: readonly StageState[] = ['review', 'awaiting-approval', 'failed', 'running', 'queued', 'denied', 'done', 'none'];

/** The state a card shows first (what needs attention). */
export function overallState(badges: readonly Badge[]): StageState {
  let best: StageState = 'none';
  for (const b of badges) if (URGENCY.indexOf(b.state) < URGENCY.indexOf(best)) best = b.state;
  return best;
}

/* ------------------------------------------------------------------ */
/* Clips                                                               */

/** The clip a shot plays in the studio: post > final > engine > animatic (finished first, then in review). */
export function shotClip(dir: string, shot: Shot, pieces: Pieces): { stage: ShotStage; path: string; state: StageState } | null {
  const order: ShotStage[] = shot.type === 'engine' ? ['engine'] : ['post', 'final', 'animatic'];
  for (const want of ['done', 'review'] as const)
    for (const stage of order) if (pieceState(pieces, `shot:${shot.id}:${stage}`) === want) return { stage, path: videoPaths[stage](dir, shot.id), state: want };
  return null;
}

/** A still for the shot card: keyframe, else the last frame of its clip. */
export function shotThumb(dir: string, shot: Shot, pieces: Pieces): string | null {
  if (shot.keyframe) return shot.keyframe;
  const k = pieceState(pieces, `shot:${shot.id}:keyframe`);
  if (k === 'done' || k === 'review') return videoPaths.keyframe(dir, shot.id);
  const clip = shotClip(dir, shot, pieces);
  return clip ? videoPaths.lastFrame(clip.path) : null;
}

/** The identity image of a character: given reference or generated sheet. */
export function characterImage(dir: string, c: Character, pieces: Pieces): string | null {
  if (c.refImages?.[0]) return c.refImages[0];
  const s = pieceState(pieces, `character:${c.id}:sheet`);
  return s === 'done' || s === 'review' ? videoPaths.sheet(dir, c.id) : null;
}

/* ------------------------------------------------------------------ */
/* Playhead                                                            */

/** The shot under timeline time `t` (the later shot wins inside a crossfade) and the time inside it. */
export function timelineAt(entries: readonly TimelineEntry[], t: number): { index: number; entry: TimelineEntry; local: number } | null {
  if (!entries.length) return null;
  let index = 0;
  for (let i = 0; i < entries.length; i++) if (entries[i]!.start <= t) index = i;
  const entry = entries[index]!;
  return { index, entry, local: Math.max(0, Math.min(entry.duration, t - entry.start)) };
}

/** Clamp + snap a time to the frame grid. */
export function snapTime(t: number, total: number, fps: number): number {
  const c = Math.max(0, Math.min(total, t));
  return Math.round(c * fps) / fps;
}

/** "1:02.5" timecode. */
export function timecode(t: number): string {
  const s = Math.max(0, t);
  const m = Math.floor(s / 60);
  const rest = s - m * 60;
  return `${m}:${rest < 10 ? '0' : ''}${rest.toFixed(1)}`;
}

/* ------------------------------------------------------------------ */
/* Audio lanes                                                         */

export type Lane = 'dialogue' | 'music' | 'sfx' | 'ambience';

export interface LaneItem {
  readonly lane: Lane;
  readonly key: string;
  readonly label: string;
  readonly start: number;
  readonly end: number;
  readonly file: string;
  readonly state: StageState;
}

/** Every audio piece on the timeline (dialogue at its spoken time, cues at their offsets, music ranges). */
export function laneItems(doc: VideoDoc, dir: string, entries: readonly TimelineEntry[], pieces: Pieces, total: number): LaneItem[] {
  const names = new Map(doc.characters.map((c) => [c.id, c.name]));
  const out: LaneItem[] = [];
  for (const l of lineTimings(doc)) {
    const key = `line:${l.shotId}:${l.lineId}:voice`;
    out.push({ lane: 'dialogue', key, label: `${names.get(l.character) ?? l.character}: ${l.text}`, start: l.start, end: l.end, file: videoPaths.voice(dir, l.shotId, l.lineId), state: pieceState(pieces, key) });
  }
  allShots(doc).forEach((ref, i) => {
    const e = entries[i];
    if (!e) return;
    for (const c of ref.shot.sfx ?? []) {
      const key = `sfx:${ref.shot.id}:${c.id}`;
      const start = e.start + (c.offset ?? 0);
      out.push({ lane: 'sfx', key, label: c.prompt || c.preset || c.id, start, end: start + (c.duration ?? 4), file: videoPaths.sfx(dir, ref.shot.id, c.id), state: pieceState(pieces, key) });
    }
  });
  for (const m of doc.audio.music) out.push({ lane: 'music', key: `music:${m.id}`, label: m.prompt || m.id, start: m.start, end: m.end ?? total, file: videoPaths.music(dir, m.id), state: pieceState(pieces, `music:${m.id}`) });
  for (const a of doc.audio.ambience) out.push({ lane: 'ambience', key: `ambience:${a.id}`, label: a.prompt || a.id, start: a.start, end: a.end ?? total, file: videoPaths.ambience(dir, a.id), state: pieceState(pieces, `ambience:${a.id}`) });
  return out;
}

/** Finished audio items that start in [t0, t1) (the studio player starts them as the playhead passes). */
export function audioStartsBetween(items: readonly LaneItem[], t0: number, t1: number): LaneItem[] {
  return items.filter((i) => i.state === 'done' && i.start >= t0 && i.start < t1);
}

/** Finished items playing at `t` with their offset (seeking starts them in the middle). */
export function audioPlayingAt(items: readonly LaneItem[], t: number): { item: LaneItem; offset: number }[] {
  return items.filter((i) => i.state === 'done' && i.start <= t && t < i.end).map((item) => ({ item, offset: t - item.start }));
}

/* ------------------------------------------------------------------ */
/* Engine preview (Scene view sequence director)                        */

/** Commands that show timeline time `t` of the video's sequence in the Scene view. */
export function sequencePreviewCommands(sequence: string, t: number, loaded: string | null | undefined): ({ cmd: 'load'; path: string } | { cmd: 'previewCamera'; on: boolean } | { cmd: 'seek'; time: number })[] {
  const out: ({ cmd: 'load'; path: string } | { cmd: 'previewCamera'; on: boolean } | { cmd: 'seek'; time: number })[] = [];
  if (loaded !== sequence) out.push({ cmd: 'load', path: sequence }, { cmd: 'previewCamera', on: true });
  out.push({ cmd: 'seek', time: Math.round(t * 1000) / 1000 });
  return out;
}

/* ------------------------------------------------------------------ */
/* Generator choices                                                   */

export function generatorChoiceLabel(g: GeneratorInfo): string {
  return `${g.label} · ${SPEED_LABEL[g.speed] ?? ''} · ${QUALITY_LABEL[g.quality] ?? ''} quality`;
}

const choice = (g: GeneratorInfo) => ({ value: g.id, label: generatorChoiceLabel(g) });

export function generatorChoices(stage: 'animatic' | 'final' | 'post' | 'keyframe'): { value: string; label: string }[] {
  if (stage === 'keyframe') return PIXEL_GENERATORS.filter((g) => ['photo-realvis-fast', 'photo-realvis', 'photo-sdxl', 'image-sdxl'].includes(g.id)).map(choice);
  const ids = stage === 'animatic' ? ['animatic', 'shot'] : stage === 'final' ? ['shot', 'shot-hq', 'shot-bridge', 'animatic'] : ['interpolate', 'interpolate-film', 'upscale-fast', 'upscale'];
  return ids.flatMap((id) => VIDEO_GENERATORS.filter((g) => g.id === id)).map(choice);
}

/* ------------------------------------------------------------------ */
/* Storyboard edits (immutable)                                        */

/** A free id: `prefix` + the lowest unused number. */
export function nextId(prefix: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  for (let i = 1; ; i++) if (!used.has(`${prefix}${i}`)) return `${prefix}${i}`;
}

const mapScenes = (doc: VideoDoc, fn: (scene: VideoScene, act: Act) => VideoScene): VideoDoc => ({ ...doc, acts: doc.acts.map((a) => ({ ...a, scenes: a.scenes.map((s) => fn(s, a)) })) });

export function updateShot(doc: VideoDoc, id: string, patch: Partial<Shot>): VideoDoc {
  return mapScenes(doc, (s) => (s.shots.some((x) => x.id === id) ? { ...s, shots: s.shots.map((x) => (x.id === id ? ({ ...x, ...patch } as Shot) : x)) } : s));
}

export function addShot(doc: VideoDoc, sceneId: string, after?: string): { doc: VideoDoc; id: string } {
  const id = nextId('s', allShots(doc).map((r) => r.shot.id));
  const shot: Shot = { id, duration: 5, type: doc.mode === 'engine' ? 'engine' : 'ai', description: '', ...(doc.mode === 'engine' ? { camera: { entity: 'camera' } } : {}) };
  return {
    id,
    doc: mapScenes(doc, (s) => {
      if (s.id !== sceneId) return s;
      const shots = [...s.shots];
      const at = after ? shots.findIndex((x) => x.id === after) : -1;
      shots.splice(at >= 0 ? at + 1 : shots.length, 0, shot);
      return { ...s, shots };
    }),
  };
}

export function removeShot(doc: VideoDoc, id: string): VideoDoc {
  return mapScenes(doc, (s) => ({ ...s, shots: s.shots.filter((x) => x.id !== id) }));
}

/** Moves a shot by `delta` places inside its scene. */
export function moveShot(doc: VideoDoc, id: string, delta: number): VideoDoc {
  return mapScenes(doc, (s) => {
    const i = s.shots.findIndex((x) => x.id === id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= s.shots.length) return s;
    const shots = [...s.shots];
    const [shot] = shots.splice(i, 1);
    shots.splice(j, 0, shot!);
    return { ...s, shots };
  });
}

export function addScene(doc: VideoDoc, actId?: string): { doc: VideoDoc; id: string } {
  const id = nextId('sc', doc.acts.flatMap((a) => a.scenes.map((s) => s.id)));
  const scene: VideoScene = { id, title: `Scene ${id.slice(2)}`, shots: [] };
  if (!doc.acts.length) return { id, doc: { ...doc, acts: [{ id: 'a1', title: 'Act 1', scenes: [scene] }] } };
  const target = actId ?? doc.acts.at(-1)!.id;
  return { id, doc: { ...doc, acts: doc.acts.map((a) => (a.id === target ? { ...a, scenes: [...a.scenes, scene] } : a)) } };
}

export function updateScene(doc: VideoDoc, id: string, patch: Partial<VideoScene>): VideoDoc {
  return mapScenes(doc, (s) => (s.id === id ? { ...s, ...patch } : s));
}

export function addCharacter(doc: VideoDoc): { doc: VideoDoc; id: string } {
  const id = nextId('char', doc.characters.map((c) => c.id));
  return { id, doc: { ...doc, characters: [...doc.characters, { id, name: `Character ${id.slice(4)}`, description: '', voice: { engine: 'kokoro', voice: 'af_heart' } }] } };
}

export function updateCharacter(doc: VideoDoc, id: string, patch: Partial<Character>): VideoDoc {
  return { ...doc, characters: doc.characters.map((c) => (c.id === id ? { ...c, ...patch } : c)) };
}

export function updateLine(doc: VideoDoc, shotId: string, lineId: string, patch: Partial<DialogueLine> | null): VideoDoc {
  const shot = allShots(doc).find((r) => r.shot.id === shotId)?.shot;
  if (!shot) return doc;
  const dialogue = patch === null ? (shot.dialogue ?? []).filter((l) => l.id !== lineId) : (shot.dialogue ?? []).map((l) => (l.id === lineId ? { ...l, ...patch } : l));
  return updateShot(doc, shotId, { dialogue });
}

export function addLine(doc: VideoDoc, shotId: string): VideoDoc {
  const shot = allShots(doc).find((r) => r.shot.id === shotId)?.shot;
  if (!shot) return doc;
  const id = nextId('l', (shot.dialogue ?? []).map((l) => l.id));
  const character = shot.characters?.[0] ?? doc.characters[0]?.id ?? '';
  return updateShot(doc, shotId, { dialogue: [...(shot.dialogue ?? []), { id, character, text: '' }] });
}

export function updateCue(doc: VideoDoc, shotId: string, cueId: string, patch: Partial<SfxCue> | null): VideoDoc {
  const shot = allShots(doc).find((r) => r.shot.id === shotId)?.shot;
  if (!shot) return doc;
  const sfx = patch === null ? (shot.sfx ?? []).filter((c) => c.id !== cueId) : (shot.sfx ?? []).map((c) => (c.id === cueId ? { ...c, ...patch } : c));
  return updateShot(doc, shotId, { sfx });
}

export function addCue(doc: VideoDoc, shotId: string): VideoDoc {
  const shot = allShots(doc).find((r) => r.shot.id === shotId)?.shot;
  if (!shot) return doc;
  const id = nextId('fx', (shot.sfx ?? []).map((c) => c.id));
  return updateShot(doc, shotId, { sfx: [...(shot.sfx ?? []), { id, prompt: '', offset: 0 }] });
}

/** Text the "Plan with Assistant" button sends: the request plus where to write it. */
export function planPrompt(path: string, doc: VideoDoc, prompt: string): string {
  const lines = [
    `Plan the video ${path} in the Video Studio (${doc.mode} mode, ${Math.round(doc.targetLength)} s, ${doc.width}x${doc.height} @ ${doc.fps} fps).`,
    prompt.trim() ? `What it is about: ${prompt.trim()}` : doc.prompt ? `What it is about: ${doc.prompt}` : '',
    'Write the story bible, the characters (precise look and a voice each), locations, acts, scenes and every shot (camera, characters, dialogue with emotion, sound cues, continuity, transitions) and the music (style and lyrics) with prism_video_storyboard so the shots add up to the target length and keep one story from start to end.',
    doc.mode !== 'ai' ? 'Engine shots need a camera entity of the scene and animation clips; write the sequence with prism_video_sequence.' : '',
    'Then render the animatic (prism_video_render preset "animatic") and tell me when it is ready for review.',
  ];
  return lines.filter(Boolean).join('\n');
}
